/**
 * airan-ntn — Main Browser Entry Point
 * UI binding, config reading, animation loop
 */

import { AiranNtnSimulator } from './simulator.js';
import { SimConfig, DEFAULT_CONFIG, RoundMetrics, SimState, UserEquipment } from './types.js';
import { renderTopology, renderCharts, renderSNRBars, createViewState, setRenderState, ViewState } from './renderer.js';
import { SIM_PRESETS } from './config.js';

// ──────────────────────────────────────────────────────────────────────────────
// DOM Elements
// ──────────────────────────────────────────────────────────────────────────────

const topoCanvas = document.getElementById('topo') as HTMLCanvasElement;
const chartCanvas = document.getElementById('chart') as HTMLCanvasElement;
const snrCanvas = document.getElementById('snr-bars') as HTMLCanvasElement;
const runBtn = document.getElementById('run-btn') as HTMLButtonElement;
const rstBtn = document.getElementById('rst-btn') as HTMLButtonElement;
const phaseEl = document.getElementById('phase') as HTMLSpanElement;
const roundEl = document.getElementById('round-num') as HTMLSpanElement;
const totalEl = document.getElementById('total-rounds') as HTMLSpanElement;
const lossEl = document.getElementById('stat-loss') as HTMLSpanElement;
const accEl = document.getElementById('stat-acc') as HTMLSpanElement;
const snrEl = document.getElementById('stat-snr') as HTMLSpanElement;
const selEl = document.getElementById('stat-sel') as HTMLSpanElement;
const commEl = document.getElementById('stat-comm') as HTMLSpanElement;
const elevEl = document.getElementById('stat-elev') as HTMLSpanElement;
const dopplerEl = document.getElementById('stat-doppler') as HTMLSpanElement;
const feederEl = document.getElementById('stat-feeder') as HTMLSpanElement;
const speedSlider = document.getElementById('speed') as HTMLInputElement;
const speedVal = document.getElementById('speed-val') as HTMLSpanElement;
const csvBtn = document.getElementById('csv-btn') as HTMLButtonElement;
const presetSel = document.getElementById('preset') as HTMLSelectElement;

// ──────────────────────────────────────────────────────────────────────────────
// Simulator Instance
// ──────────────────────────────────────────────────────────────────────────────

let sim: AiranNtnSimulator | null = null;
let animFrame: number | null = null;
let msPerRound = 300;
let viewState: ViewState;

// ──────────────────────────────────────────────────────────────────────────────
// Config Reading
// ──────────────────────────────────────────────────────────────────────────────

function readConfig(): SimConfig {
  const v = (id: string) => (document.getElementById(id) as HTMLInputElement | HTMLSelectElement)?.value;
  const n = (id: string) => parseFloat(v(id) ?? '0');
  const b = (id: string) => (document.getElementById(id) as HTMLInputElement)?.checked ?? false;

  // Parse enabled xApps from multi-select
  const xappSel = document.getElementById('cfg-xapps') as HTMLSelectElement;
  const enabledXApps = Array.from(xappSel?.selectedOptions || []).map(o => o.value) as SimConfig['enabledXApps'];

  return {
    // Constellation
    numOrbitals: n('cfg-orbitals'),
    numPlanes: n('cfg-planes'),
    inclination: n('cfg-inclination'),
    altitude: n('cfg-altitude'),
    orbitType: v('cfg-orbittype') as SimConfig['orbitType'],
    // ISL
    enableISL: b('cfg-isl'),
    maxISLRange: n('cfg-islrange'),
    islType: v('cfg-isltype') as SimConfig['islType'],
    // Ground stations
    numGroundStations: n('cfg-gs'),
    gsPositions: DEFAULT_CONFIG.gsPositions,
    // Channel
    carrierFreqGHz: n('cfg-freq'),
    bandwidthMHz: n('cfg-bw'),
    channelModel: v('cfg-channel') as SimConfig['channelModel'],
    pathLossExponent: n('cfg-ple'),
    shadowingStd: n('cfg-shadow'),
    ricianK: n('cfg-rician'),
    snrThreshold: n('cfg-snrthr'),
    dopplerEnabled: b('cfg-doppler'),
    // Mobility
    numUEs: n('cfg-ues'),
    ueSpeed: n('cfg-uespeed'),
    walkEnabled: b('cfg-walk'),
    walkSpeed: n('cfg-walkspeed'),
    roundDuration: n('cfg-rounddur'),
    levyAlpha: n('cfg-levyalpha'),
    levyBeta: n('cfg-levybeta'),
    levyMinStep: n('cfg-levymin'),
    // FL
    flAlgorithm: v('cfg-fl') as SimConfig['flAlgorithm'],
    scheduler: v('cfg-sched') as SimConfig['scheduler'],
    localEpochs: n('cfg-epochs'),
    learningRate: n('cfg-lr'),
    mu: n('cfg-mu'),
    selectedPerRound: n('cfg-ksel'),
    totalRounds: n('cfg-rounds'),
    numFeatures: n('cfg-features'),
    numClasses: n('cfg-classes'),
    samplesPerUE: n('cfg-samples'),
    nonIIDAlpha: n('cfg-alpha'),
    bsCooperation: b('cfg-coop'),
    // NTN-specific FL
    ntnAwareScheduling: b('cfg-ntn-sched'),
    minElevationDeg: n('cfg-minelev'),
    maxDopplerHz: n('cfg-maxdoppler'),
    requireFeederLink: b('cfg-feederreq'),
    // Space RIC
    spaceRicEnabled: b('cfg-spaceric'),
    autonomousMode: b('cfg-auto'),
    feederOutageThreshold: n('cfg-feederthr'),
    modelSyncInterval: n('cfg-syncint'),
    islCoordination: b('cfg-islcoord'),
    gradientCompression: n('cfg-gradcomp'),
    eclipseAwareFl: b('cfg-eclipsefl'),
    // xApps
    enabledXApps,
    // Simulation
    roundDurationMs: n('cfg-speedms'),
    seed: n('cfg-seed')
  };
}

// ──────────────────────────────────────────────────────────────────────────────
// Slider Binding
// ──────────────────────────────────────────────────────────────────────────────

function bindSlider(id: string, valId: string, fmt: (v: number) => string = v => String(v)): void {
  const el = document.getElementById(id) as HTMLInputElement;
  const lbl = document.getElementById(valId) as HTMLSpanElement;
  if (!el || !lbl) return;
  const update = () => { lbl.textContent = fmt(parseFloat(el.value)); };
  el.addEventListener('input', update);
  update();
}

// ──────────────────────────────────────────────────────────────────────────────
// Initialization
// ──────────────────────────────────────────────────────────────────────────────

function initSim(): void {
  const cfg = readConfig();
  sim = new AiranNtnSimulator(cfg);
  setRenderState(sim.getState());

  totalEl.textContent = String(cfg.totalRounds);
  roundEl.textContent = '0';
  phaseEl.textContent = 'idle';
  phaseEl.className = 'hdr-phase';
  lossEl.textContent = '—';
  accEl.textContent = '—';
  snrEl.textContent = '—';
  selEl.textContent = '—';
  commEl.textContent = '—';
  elevEl.textContent = '—';
  dopplerEl.textContent = '—';
  feederEl.textContent = '—';

  // Setup orbit type listener (DOM is ready here)
  setupOrbitTypeListener();

  // Resize canvases
  resizeCanvases();
  renderTopology(topoCanvas.getContext('2d')!, sim.getState(), viewState);
  chartCanvas.getContext('2d')!.clearRect(0, 0, chartCanvas.width, chartCanvas.height);
  snrCanvas.getContext('2d')!.clearRect(0, 0, snrCanvas.width, snrCanvas.height);
}

function resizeCanvases(): void {
  const dpi = window.devicePixelRatio || 1;
  [topoCanvas, chartCanvas, snrCanvas].forEach(c => {
    c.width = c.clientWidth * dpi;
    c.height = c.clientHeight * dpi;
    c.getContext('2d')!.scale(dpi, dpi);
  });
  viewState = createViewState(topoCanvas);
}

window.addEventListener('resize', () => {
  resizeCanvases();
  if (sim) renderTopology(topoCanvas.getContext('2d')!, sim.getState(), viewState);
});

// ──────────────────────────────────────────────────────────────────────────────
// UI Update
// ──────────────────────────────────────────────────────────────────────────────

function updateUI(m: RoundMetrics): void {
  const state = sim!.getState();
  roundEl.textContent = String(m.round);
  lossEl.textContent = m.globalLoss.toFixed(4);
  accEl.textContent = (m.globalAccuracy * 100).toFixed(1) + '%';
  snrEl.textContent = m.avgSNR.toFixed(1) + ' dB';
  selEl.textContent = `${m.selectedIds.length}/${state.ues.length}`;
  commEl.textContent = (m.commCostBits / 1e6).toFixed(2) + ' Mbits';
  elevEl.textContent = m.avgElevation.toFixed(1) + '°';
  dopplerEl.textContent = (m.avgDoppler / 1000).toFixed(1) + ' kHz';
  feederEl.textContent = (m.feederLinkUptime * 100).toFixed(0) + '%';

  renderTopology(topoCanvas.getContext('2d')!, state, viewState);
  renderCharts(chartCanvas.getContext('2d')!, state.history, chartCanvas.width, chartCanvas.height);
  renderSNRBars(snrCanvas.getContext('2d')!, state.ues, 0, 0, snrCanvas.width, snrCanvas.height);

  if (m.round >= state.config.totalRounds) {
    phaseEl.textContent = 'done';
    phaseEl.className = 'hdr-phase agg';
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// Animation Loop
// ──────────────────────────────────────────────────────────────────────────────

function tick(): void {
  if (!sim || sim.isDone) {
    phaseEl.textContent = 'done';
    phaseEl.className = 'hdr-phase agg';
    runBtn.textContent = '▶ START';
    runBtn.disabled = false;
    animFrame = null;
    return;
  }

  phaseEl.textContent = 'training';
  phaseEl.className = 'hdr-phase upload';

  const m = sim.step();
  updateUI(m);

  animFrame = setTimeout(tick, msPerRound) as unknown as number;
}

// ──────────────────────────────────────────────────────────────────────────────
// Event Handlers
// ──────────────────────────────────────────────────────────────────────────────

runBtn.addEventListener('click', () => {
  if (animFrame) {
    clearTimeout(animFrame);
    animFrame = null;
    runBtn.textContent = '▶ START';
    runBtn.disabled = false;
    phaseEl.textContent = 'paused';
    phaseEl.className = 'hdr-phase';
  } else {
    if (sim?.isDone) initSim();
    runBtn.textContent = '⏸ PAUSE';
    runBtn.disabled = false;
    tick();
  }
});

rstBtn.addEventListener('click', () => {
  if (animFrame) {
    clearTimeout(animFrame);
    animFrame = null;
  }
  initSim();
  runBtn.textContent = '▶ START';
  runBtn.disabled = false;
});

speedSlider.addEventListener('input', () => {
  msPerRound = 1000 / parseFloat(speedSlider.value);
  speedVal.textContent = speedSlider.value + '×';
});

// ──────────────────────────────────────────────────────────────────────────────
// Globe Rotation (Mouse Drag)
// ──────────────────────────────────────────────────────────────────────────────

topoCanvas.addEventListener('mousedown', (e) => {
  if (viewState) {
    viewState.isDragging = true;
    viewState.lastMousePos = { x: e.clientX, y: e.clientY };
    topoCanvas.style.cursor = 'grabbing';
  }
});

window.addEventListener('mousemove', (e) => {
  if (viewState && viewState.isDragging && viewState.lastMousePos) {
    const dx = e.clientX - viewState.lastMousePos.x;
    const dy = e.clientY - viewState.lastMousePos.y;
    viewState.rotation += dx * 0.005;
    viewState.pitch = Math.max(-Math.PI / 2 + 0.1, Math.min(Math.PI / 2 - 0.1, viewState.pitch - dy * 0.005));
    viewState.lastMousePos = { x: e.clientX, y: e.clientY };

    // Re-render
    if (sim) {
      const state = sim.getState();
      setRenderState(state);
      renderTopology(topoCanvas.getContext('2d')!, state, viewState);
      renderCharts(chartCanvas.getContext('2d')!, state.history, chartCanvas.width, chartCanvas.height);
      renderSNRBars(snrCanvas.getContext('2d')!, state.ues, 0, 0, snrCanvas.width, snrCanvas.height);
    }
  }
});

window.addEventListener('mouseup', () => {
  if (viewState) {
    viewState.isDragging = false;
    viewState.lastMousePos = null;
    topoCanvas.style.cursor = 'grab';
  }
});

topoCanvas.addEventListener('mouseleave', () => {
  if (viewState) {
    viewState.isDragging = false;
    viewState.lastMousePos = null;
    topoCanvas.style.cursor = 'grab';
  }
});

// Touch support for mobile
topoCanvas.addEventListener('touchstart', (e) => {
  if (viewState && e.touches.length === 1) {
    viewState.isDragging = true;
    viewState.lastMousePos = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }
}, { passive: false });

window.addEventListener('touchmove', (e) => {
  if (viewState && viewState.isDragging && viewState.lastMousePos && e.touches.length === 1) {
    e.preventDefault();
    const dx = e.touches[0].clientX - viewState.lastMousePos.x;
    const dy = e.touches[0].clientY - viewState.lastMousePos.y;
    viewState.rotation += dx * 0.005;
    viewState.pitch = Math.max(-Math.PI / 2 + 0.1, Math.min(Math.PI / 2 - 0.1, viewState.pitch - dy * 0.005));
    viewState.lastMousePos = { x: e.touches[0].clientX, y: e.touches[0].clientY };

    if (sim) {
      const state = sim.getState();
      setRenderState(state);
      renderTopology(topoCanvas.getContext('2d')!, state, viewState);
      renderCharts(chartCanvas.getContext('2d')!, state.history, chartCanvas.width, chartCanvas.height);
      renderSNRBars(snrCanvas.getContext('2d')!, state.ues, 0, 0, snrCanvas.width, snrCanvas.height);
    }
  }
}, { passive: false });

window.addEventListener('touchend', () => {
  if (viewState) {
    viewState.isDragging = false;
    viewState.lastMousePos = null;
  }
});

// Also set initial cursor
topoCanvas.style.cursor = 'grab';

presetSel.addEventListener('change', () => {
  const preset = SIM_PRESETS[presetSel.value as keyof typeof SIM_PRESETS];
  if (preset) {
    // Apply preset to UI controls
    applyPreset(preset);
  }
});

// ──────────────────────────────────────────────────────────────────────────────
// Orbit Type ↔ Altitude Correlation (moved inside initSim for DOM readiness)
// ──────────────────────────────────────────────────────────────────────────────

// Orbit type constants (module-level for reuse)
const ORBIT_ALTITUDES = {
  LEO: 550,
  MEO: 20000,
  GEO: 35786
} as const;

type OrbitTypeKey = keyof typeof ORBIT_ALTITUDES;

function updateAltitudeForOrbitType(orbitType: string): void {
  const altitudeInput = document.getElementById('cfg-altitude') as HTMLInputElement;
  const altitudeVal = document.getElementById('cfg-altitude-val') as HTMLSpanElement;
  const key = orbitType as OrbitTypeKey;
  const altitude = ORBIT_ALTITUDES[key] ?? 550;

  if (altitudeInput && altitudeVal) {
    altitudeInput.value = String(altitude);
    altitudeInput.min = key === 'GEO' ? '35786' : key === 'MEO' ? '8000' : '300';
    altitudeInput.max = key === 'GEO' ? '35786' : key === 'MEO' ? '25000' : '2000';
    altitudeInput.disabled = key === 'GEO';
    altitudeInput.dispatchEvent(new Event('input'));
  }
}

function setupOrbitTypeListener(): void {
  const orbitTypeSel = document.getElementById('cfg-orbittype') as HTMLSelectElement;
  if (orbitTypeSel) {
    orbitTypeSel.addEventListener('change', (e) => {
      updateAltitudeForOrbitType((e.target as HTMLSelectElement).value);
    });
    // Initialize altitude on load
    updateAltitudeForOrbitType(orbitTypeSel.value);
  }
}

// Also update when preset changes - moved inside initSim
let applyPresetWrapper: (preset: SimConfig) => void = applyPreset;

applyPreset = function(preset: SimConfig): void {
  applyPresetWrapper(preset);
  // Update orbit type selector to match preset
  const orbitTypeSel = document.getElementById('cfg-orbittype') as HTMLSelectElement;
  if (orbitTypeSel) {
    orbitTypeSel.value = preset.orbitType;
    updateAltitudeForOrbitType(preset.orbitType);
  }
};

csvBtn.addEventListener('click', () => {
  if (!sim) return;
  const history = sim.getState().history;
  const headers = ['round', 'loss', 'accuracy', 'avgSNR', 'minSNR', 'participation', 'commBits', 'avgElev', 'avgDoppler', 'feederUptime', 'handovers', 'satEnergy'];
  const rows = history.map(m => [
    m.round, m.globalLoss, m.globalAccuracy, m.avgSNR, m.minSNR,
    m.participationRate, m.commCostBits, m.avgElevation, m.avgDoppler,
    m.feederLinkUptime, m.handovers, m.satelliteEnergy
  ]);
  const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `airan-ntn-rounds-${Date.now()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
});

let applyPreset = function(preset: SimConfig): void {
  const setVal = (id: string, val: number | string | boolean) => {
    const el = document.getElementById(id) as HTMLInputElement | HTMLSelectElement;
    if (el) {
      if (el.type === 'checkbox') (el as HTMLInputElement).checked = val as boolean;
      else el.value = String(val);
      el.dispatchEvent(new Event('input'));
    }
  };

  setVal('cfg-orbitals', preset.numOrbitals);
  setVal('cfg-planes', preset.numPlanes);
  setVal('cfg-inclination', preset.inclination);
  setVal('cfg-altitude', preset.altitude);
  setVal('cfg-orbittype', preset.orbitType);
  setVal('cfg-ues', preset.numUEs);
  setVal('cfg-rounds', preset.totalRounds);
  setVal('cfg-ksel', preset.selectedPerRound);
  setVal('cfg-epochs', preset.localEpochs);
  setVal('cfg-lr', preset.learningRate);
  setVal('cfg-fl', preset.flAlgorithm);
  setVal('cfg-sched', preset.scheduler);
  setVal('cfg-speedms', preset.roundDurationMs);
  setVal('cfg-seed', preset.seed);
}

// ──────────────────────────────────────────────────────────────────────────────
// Bind Sliders
// ──────────────────────────────────────────────────────────────────────────────

bindSlider('cfg-ues', 'cfg-ues-val');
bindSlider('cfg-rounds', 'cfg-rounds-val');
bindSlider('cfg-ksel', 'cfg-ksel-val');
bindSlider('cfg-epochs', 'cfg-epochs-val');
bindSlider('cfg-lr', 'cfg-lr-val', v => v.toFixed(3));
bindSlider('cfg-speedms', 'cfg-speedms-val', v => v + 'ms');
bindSlider('cfg-seed', 'cfg-seed-val');
bindSlider('cfg-orbital', 'cfg-orbital-val');
bindSlider('cfg-planes', 'cfg-planes-val');
bindSlider('cfg-inclination', 'cfg-inclination-val', v => v + '°');
bindSlider('cfg-altitude', 'cfg-altitude-val', v => v + 'km');
bindSlider('cfg-freq', 'cfg-freq-val', v => v + 'GHz');
bindSlider('cfg-bw', 'cfg-bw-val', v => v + 'MHz');
bindSlider('cfg-levyalpha', 'cfg-levyalpha-val', v => v.toFixed(2));
bindSlider('cfg-alpha', 'cfg-alpha-val', v => v.toFixed(2));

// ──────────────────────────────────────────────────────────────────────────────
// Initialize
// ──────────────────────────────────────────────────────────────────────────────

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSim);
} else {
  initSim();
}
console.log('airan-ntn simulator initialized');